import { User } from "@/store/user-store";
import type { ConditionsMatcher, ForcedSubject, MatchConditions } from "@casl/ability";
import { Ability, AbilityBuilder } from "@casl/ability";
import { AbilityProvider, Can, useAbility } from "@casl/react";
import { components } from "./api-schema";
import { Action } from "./api-schema-enums";
import { hasDevStageDeveloperAccess } from "./common/helpers/has-dev-stage-developer-access";
import {
  CAR_RUNNER_SCORE_SUBJECT,
  setCarRunnerScorePermissions,
} from "./components/error-page/_helpers/car-runner.permissions";
import { setCustomerSapExportPermissions } from "./modules/ace/customer-sap-export/customer-sap-export.permissions";
import { setCasaviContactBlacklistPermissions } from "./modules/casavi/casavi-contact-blacklist/casavi-contact-blacklist.permissions";
import { setCasaviContactWhitelistPermissions } from "./modules/casavi/casavi-contact-whitelist/casavi-contact-whitelist.permissions";
import { setCasaviSettingsPermissions } from "./modules/casavi/casavi-settings/casavi-settings.permissions";
import { setVehicleBuyInPermissions } from "./modules/fab/vehicle-buy-in/vehicle-buy-in.permissions";
import { setFitSettingsPermissions } from "./modules/fit/fit-settings/fit-settings.permissions";
import {
  setVehicleValuationPermissions,
  VEHICLE_VALUATION_SNAPSHOT_SUBJECT,
} from "./modules/fab/vehicle-valuation/vehicle-valuation.permissions";
import { setOpenPositionPermissions } from "./modules/fia/open-position/open-position.permissions";
import { setRelocationOrderPermissions } from "./modules/fit/internal/relocation-order/relocation-order.permissions";
import { setBranchPermissions } from "./modules/masterdata/branch/branch.permissions";
import { setBrandPermissions } from "./modules/masterdata/brand/brand.permissions";
import { setCompanyGroupPermissions } from "./modules/masterdata/company-group/company-group.permissions";
import { setCompanyPermissions } from "./modules/masterdata/company/company.permissions";
import { setCustomerPermissions } from "./modules/masterdata/customer/customer.permissions";
import { setDepartmentPermissions } from "./modules/masterdata/department/department.permissions";
import { setJobFunctionPermissions } from "./modules/masterdata/job-function/job-function.permissions";
import { setModelFamilyPermissions } from "./modules/masterdata/model-family/model-family.permissions";
import { setModelGenerationPermissions } from "./modules/masterdata/model-generation/model-generation.permissions";
import { setOccupationPermissions } from "./modules/masterdata/occupation/occupation.permissions";
import { setUserPermissions } from "./modules/masterdata/user/user.permissions";
import { setVehicleBlockPermissions } from "./modules/masterdata/vehicle/vehicle-block.permissions";
import {
  setVehicleSnapshotPermissions,
  VEHICLE_SNAPSHOT_SUBJECT,
} from "./modules/masterdata/vehicle/vehicle-snapshot.permissions";
import { setVehiclePermissions } from "./modules/masterdata/vehicle/vehicle.permissions";
import { setAppRolePermissions } from "./modules/ram/application/app-role/app-role.permissions";
import { setApplicationPermissions } from "./modules/ram/application/application.permissions";
import { setGroupPermissions } from "./modules/ram/group/group.permissions";
import { setRulePermissions } from "./modules/ram/group/rule/rule.permissions";
import { setTempPermissions } from "./modules/ram/temporary/temporary.permissions";

export { AbilityProvider, Can, useAbility };

type Subjects =
  | keyof components["schemas"]
  | ForcedSubject<keyof components["schemas"]>
  | typeof VEHICLE_SNAPSHOT_SUBJECT
  | typeof VEHICLE_VALUATION_SNAPSHOT_SUBJECT
  | typeof CAR_RUNNER_SCORE_SUBJECT
  | "all";

export type AppAbility = Ability<[Action, Subjects]>;

const lambdaMatcher: ConditionsMatcher<unknown> = (conditions) => {
  if (typeof conditions === "function") {
    return conditions as MatchConditions;
  }

  throw new TypeError("CASL conditions matcher expects a function condition.");
};
export function createForUser(user?: User | null) {
  const builder = new AbilityBuilder<AppAbility>(Ability);

  if (!user || !user?.roles) {
    return builder.build();
  }

  setAppRolePermissions(user, builder);
  setApplicationPermissions(user, builder);
  setGroupPermissions(user, builder);
  setRulePermissions(user, builder);
  setUserPermissions(user, builder);
  setBranchPermissions(user, builder);
  setBrandPermissions(user, builder);
  setCompanyGroupPermissions(user, builder);
  setCompanyPermissions(user, builder);
  setCustomerPermissions(user, builder);
  setDepartmentPermissions(user, builder);
  setJobFunctionPermissions(user, builder);
  setModelFamilyPermissions(user, builder);
  setModelGenerationPermissions(user, builder);
  setOccupationPermissions(user, builder);
  setVehiclePermissions(user, builder);
  setVehicleBlockPermissions(user, builder);
  setVehicleSnapshotPermissions(user, builder);
  setVehicleBuyInPermissions(user, builder);
  setVehicleValuationPermissions(user, builder);
  setRelocationOrderPermissions(user, builder);
  setFitSettingsPermissions(user, builder);
  setTempPermissions(user, builder);
  setCasaviContactWhitelistPermissions(user, builder);
  setCasaviContactBlacklistPermissions(user, builder);
  setCasaviSettingsPermissions(user, builder);
  setCustomerSapExportPermissions(user, builder);
  setOpenPositionPermissions(user, builder);
  setCarRunnerScorePermissions(user, builder);

  if (hasDevStageDeveloperAccess(user.roles)) {
    builder.can(Action.Manage, "all");
  }

  return builder.build({ conditionsMatcher: lambdaMatcher });
}
